import React from 'react'
import styled from 'styled-components'
import { Alert, Block, Button, Checkbox, Spinner } from '@mote/ui'
import { useAccountNotificationPreferences } from '@mote/api'
import { Content } from '../Dashboard/components'

const OPT_OUT_KEY = 'opt-out-all'
const DEFAULT_ERROR =
  'Sorry, we could not load your notification preferences at this time. Please try again or contact support@mote.com'

export const AccountNotificationPreferences = () => {
  const [updateMessage, setUpdateMessage] = React.useState('')
  const [optOut, setOptOut] = React.useState(false)
  const [notificationPreferences, setNotificationPreferences] =
    React.useState(null)
  const [networkError, setNetworkError] = React.useState('')
  const [initialLoading, setInitialLoading] = React.useState(true)
  const { mutate, loading } = useAccountNotificationPreferences()

  React.useEffect(() => {
    async function fetchNotificationPreferences() {
      try {
        const res = await mutate({ variables: { mode: 'get' } })
        setInitialLoading(false)

        if (res.error) {
          setNetworkError(DEFAULT_ERROR)
          return
        }

        if (res.data?.notificationPreferences) {
          setNotificationPreferences(res.data?.notificationPreferences)
        }
      } catch (error) {
        console.error(error)
        setNetworkError(DEFAULT_ERROR)
      }
    }
    fetchNotificationPreferences()
  }, [])

  function updatePreference(key, checked) {
    if (updateMessage) {
      setUpdateMessage(null)
    }
    const preferences = Object.assign({}, notificationPreferences)

    preferences.map[key].email = checked
    if (key === OPT_OUT_KEY && checked) {
      setOptOut(true)

      for (let otherKey of Object.keys(preferences.map)) {
        if (otherKey !== OPT_OUT_KEY) {
          preferences.map[otherKey].email = false
        }
      }
    } else if (key === OPT_OUT_KEY && !checked && optOut) {
      setOptOut(false)
    } else if (checked && preferences.map[OPT_OUT_KEY].email) {
      setOptOut(false)
      preferences.map[OPT_OUT_KEY].email = false
    }
    setNotificationPreferences(preferences)
  }

  async function savePreferences() {
    setUpdateMessage(null)
    setNetworkError(null)

    const res = await mutate({
      variables: { mode: 'set', notificationPreferences }
    })

    if (res.error) {
      setNetworkError(
        `Sorry, we could not update your notification preferences. Please try again later or contact support@mote.com`
      )
    } else {
      setOptOut(false)
      setUpdateMessage('Your changes have been saved!')
    }
  }

  const hasPreferences = notificationPreferences && notificationPreferences.rows

  if (initialLoading) return <Spinner radius={35} />

  return (
    <Content
      type='section'
      title='Email notification settings'
      subtitle=' Which types of emails and notifications would you like to receive from us?'
      noPadding
      gap={32}
    >
      <>
        {hasPreferences && (
          <Block gap={16}>
            {notificationPreferences.rows.map(r => (
              <Checkbox
                key={r.key}
                onChange={event => {
                  const {
                    target: { checked }
                  } = event
                  updatePreference(r.key, checked)
                }}
                checked={notificationPreferences.map[r.key].email}
                label={r.strings.en}
              />
            ))}
          </Block>
        )}

        {(optOut || updateMessage || networkError) && (
          <Block gap={16} align='flex-start' maxWidth={700}>
            <Block>
              {optOut && (
                <Alert
                  message="OK, we're sad to see you go, but you can always change your mind later"
                  type='info'
                  style={{ margin: 0 }}
                />
              )}

              {updateMessage && (
                <Alert
                  type='success'
                  message={updateMessage}
                  style={{ margin: 0 }}
                />
              )}

              {networkError && (
                <Alert
                  type='error'
                  message={networkError}
                  style={{ margin: 0 }}
                />
              )}
            </Block>
          </Block>
        )}

        {!networkError && (
          <StyledButton
            loading={loading}
            text={loading ? 'Saving ...' : 'Save changes'}
            onClick={savePreferences}
            disabled={loading}
          />
        )}
      </>
    </Content>
  )
}

const StyledButton = styled(Button)`
  width: max-content;

  @media (max-width: 639px) {
    width: 100%;
  }
`
